import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { ErrorBanner } from '@/components/ui';
import {
  customerBillingService,
  type CustomerBillingOverview,
  type CustomerBillingPlan,
} from '@/api/services/customerBilling';
import { useMe } from '@/context/MeContext';
import { formatApiError } from '@/utils/formatApiError';

function formatMoney(cents: number | undefined, currency = 'EUR'): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export default function BillingPlansCustomerPage() {
  const { me } = useMe();
  const canView = me?.permissions.canViewCustomerBilling ?? false;
  const canMutate = me?.permissions.canMutateCustomerBilling ?? false;
  const [plans, setPlans] = useState<CustomerBillingPlan[]>([]);
  const [overview, setOverview] = useState<CustomerBillingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busySlug, setBusySlug] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [planRes, overviewRes] = await Promise.all([
        customerBillingService.plans(),
        customerBillingService.overview(),
      ]);
      setPlans(planRes.items);
      setOverview(overviewRes);
    } catch (e) {
      setError(formatApiError(e, 'Failed to load plans'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void load();
    else setLoading(false);
  }, [canView, load]);

  async function selectPlan(slug: string) {
    if (!canMutate) return;
    setBusySlug(slug);
    setError('');
    try {
      const result = await customerBillingService.changePlan(slug);
      const redirect = result.checkoutUrl || result.portalUrl;
      if (redirect) {
        window.location.assign(redirect);
        return;
      }
      setError(result.message || 'Plan change initiated. Waiting for payment confirmation.');
    } catch (e) {
      setError(formatApiError(e, 'Unable to start plan change'));
    } finally {
      setBusySlug(null);
    }
  }

  if (!canView) {
    return (
      <>
        <PageHeader title="Plans" subtitle="Compare available subscription plans" />
        <p className="muted">You do not have permission to view plans.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Plans" subtitle="Compare plans and upgrade or downgrade your subscription">
        <Link to="/billing" className="btn btn-ghost btn-sm">
          Back to billing
        </Link>
      </PageHeader>

      {error ? <ErrorBanner message={error} onDismiss={() => setError('')} /> : null}
      {loading ? <p className="muted">Loading plans…</p> : null}

      <div className="billing-plans-grid">
        {plans.map((plan) => (
          <article key={plan.slug} className={`card card-elevated billing-plan-card${plan.isCurrent ? ' billing-plan-card--current' : ''}`}>
            <div className="billing-plan-card__header">
              <h2>{plan.marketingTitle || plan.name}</h2>
              {plan.badge ? <span className="badge badge-info">{plan.badge}</span> : null}
              {plan.recommended ? <span className="badge badge-success">Recommended</span> : null}
              {plan.isCurrent ? <span className="badge">Current plan</span> : null}
            </div>
            <p className="billing-plan-card__price">
              {formatMoney(plan.priceCents, plan.currency)}
              <span className="muted"> / {plan.billingInterval || 'month'}</span>
            </p>
            {plan.description ? <p className="muted">{plan.description}</p> : null}
            <ul className="billing-plan-card__limits">
              <li>{plan.maxSearches ?? 'Unlimited'} searches</li>
              <li>{plan.maxNotificationRecipients ?? 'Unlimited'} recipients</li>
              <li>{plan.maxNotificationsPerDay ?? 'Unlimited'} notifications / day</li>
              {plan.historyDays ? <li>{plan.historyDays} days history</li> : null}
            </ul>
            {plan.features && plan.features.length > 0 ? (
              <ul className="billing-plan-card__features">
                {plan.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            ) : null}
            {canMutate && overview?.actions.canChangePlan && !plan.isCurrent ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={busySlug != null}
                onClick={() => void selectPlan(plan.slug)}
              >
                {busySlug === plan.slug ? 'Starting…' : 'Choose plan'}
              </button>
            ) : null}
          </article>
        ))}
      </div>
    </>
  );
}
